import { ColorsConfig } from '@ddt-web/config';
import { CellData, TabData, TableData } from '@ddt-web/data-service';
import { NumberUtil, StringUtil } from '@ddt-web/generic-utils';
import * as findIndex from 'lodash/findIndex';
import * as get from 'lodash/get';
import * as groupBy from 'lodash/groupBy';
import * as head from 'lodash/head';
import * as isNil from 'lodash/isNil';
import * as unzip from 'lodash/unzip';

import { ASSET_CLASSES } from '../constants/scenario-analysis.constant';
import { ContributorData, IChartData, IScenarioAnalysisData, IScenarioChartData, ISeriesData, LineChartSeriesCustomisations } from '../models';
import { IPieSeriesData } from '../models/ipie-series.model.interface';
import { ScatterChartSeriesCustomisations } from '../models/scatter-chart-series-customisations.interface';

export class ChartFactory {
	public static getDefaultColumnSeriesForTabData(data: TabData, categoryAsPortfolioCharacteristic?: boolean, customAttributes?: any, tableDataIndex: number = 0): Array<any> {
		const stackColumnIndices = [];
		const hasNonZeroValuesIndices = [];
		let headers;
		let cells = data.tableData[tableDataIndex].cells;
		if (categoryAsPortfolioCharacteristic) {
			headers = data.tableData[tableDataIndex].headersX;
			cells = cells.length > 0 ? cells[0].map((_, colIndex) => cells.map(row => row[colIndex])) : cells;
		} else {
			// chart categories here are portfolios names
			headers = data.displayConfig.transpose ? data.tableData[tableDataIndex].headersY : data.tableData[tableDataIndex].headersX;
			cells = cells.filter((row: Array<CellData>, index: number) => !headers[index]?.attribute?.ignoreInChart).reverse();
			headers = headers.filter(header => !header.attribute?.ignoreInChart).reverse();
		}

		const columnSeries = cells.map((row: Array<CellData>, index: number) => {
			return {
				name: headers[index]?.formattedValue,
				data: row.map((cellData: CellData, cellIndex: number) => {
					let tooltip: string;
					const contributionTooltipTabData: TabData = get(customAttributes, 'contributionTooltipTabData');
					if (contributionTooltipTabData) {
						tooltip = this.getContributionTooltip(contributionTooltipTabData, index, cellIndex, cellData, headers);
					} else {
						tooltip =
							`<div class="tooltip row text-12"><span class="col-16">${headers[index]?.formattedValue}</span>` +
							`<span class="col-8 text-align-right">${cellData.formattedValue}</span></div>`;

						if (cellData.children && cellData.children.length) {
							tooltip =
								`<div class="tooltip row text-12"><span class="col-16 text-demi">${headers[index]?.formattedValue}</span>` +
								`<span class="col-8 text-demi text-align-right">${cellData.formattedValue}</span>`;
							cellData.children.forEach((child: CellData, childIndex: number) => {
								const tooltipHeader = headers[index].children[childIndex] ? headers[index].children[childIndex].formattedValue : '';
								tooltip += `<span class="col-16">${tooltipHeader}</span>` + `<span class="col-8 text-align-right">${child.formattedValue}</span>`;
							});
							tooltip += `</div>`;
						}
					}
					if (!stackColumnIndices[cellIndex] && !categoryAsPortfolioCharacteristic) {
						if (Number(cellData.rawValue) > 0) {
							stackColumnIndices[cellIndex] = cellData.rawValue;
							return { y: cellData.rawValue, tooltip: tooltip, firstColumnStack: true };
						} else if (cellData.rawValue) {
							// there might be only negative values per portfolio
							hasNonZeroValuesIndices[cellIndex] = true;
						}
					}
					return {
						y: NumberUtil.isNumericalValue(String(cellData.rawValue)) ? Number(cellData.rawValue) : cellData.rawValue, tooltip: tooltip
					};
				})
			};
		});
		for (let i = 0; i < data.tableData[tableDataIndex].headersX.length; i++) {
			if (!stackColumnIndices[i] && hasNonZeroValuesIndices[i]) {
				// if firstColumnStack is not set for an index with non-zero/negative values find the max value and set
				const flattenedSeries = columnSeries.map(elem => elem?.data[i]?.y ?? -Infinity);
				const categoryIndexWithMaxValue: number = flattenedSeries.indexOf(Math.max.apply(Math, flattenedSeries));
				if (columnSeries[categoryIndexWithMaxValue]?.data?.[i]) {
					columnSeries[categoryIndexWithMaxValue].data[i].firstColumnStack = true;
				}
			}
		}
		return columnSeries;
	}

	public static getExposureColumnSeriesForTabData(data: TabData, customAttributes?: any): Array<any> {
		if (data?.tableData[0]) {
			let headers = data.displayConfig.transpose ? data.tableData[0].headersY : data.tableData[0].headersX;
			const series = [];
			const cells = data.tableData[0].cells
				.filter((row: Array<CellData>, index: number) => !headers[index].attribute?.ignoreInChart);
			headers = headers.filter(header => !header.attribute?.ignoreInChart).slice().reverse();

			cells.slice()
				.reverse()
				.forEach((row: Array<CellData>, index: number) => {
					if (headers[index].parent) {
						const existingPoint = series.find(point => point.name === headers[index].parent[0].formattedValue);
						if (existingPoint) {
							existingPoint.data.forEach((dataPoint, existingIndex) => {
								dataPoint.tooltip +=
									`<span class="col-16">${headers[index].formattedValue}</span>` + `<span class="col-8 text-align-right">${row[existingIndex].formattedValue}</span>`;
							});
						}
					} else {
						series.push({
							name: headers[index].formattedValue,
							data: row.map((cellData: CellData, cellIndex: number) => {
								let tooltip: string;
								const contributionTooltipTabData: TabData = get(customAttributes, 'contributionTooltipTabData');
								if (contributionTooltipTabData) {
									tooltip = this.getContributionTooltip(contributionTooltipTabData, index, cellIndex, cellData, headers);
								} else {
									tooltip =
										`<div class="tooltip row text-12"><span class="col-16 text-demi text-bold">${headers[index].formattedValue}</span>` +
										`<span class="col-8 text-align-right">${cellData.formattedValue}</span>`;
								}
								return {
									y: cellData.rawValue,
									tooltip: tooltip
								};
							})
						});
					}
				});
			series.forEach(point => {
				point.data.forEach(dataPoint => {
					dataPoint.tooltip += `</div>`;
				});
			});
			return series;
		}
	}

	public static getStackedSeriesForTabData(data: TabData, portfolioNames?: string[], multiplier: number = 1): Array<any> {
		const cellsData = unzip(data?.tableData?.[0]?.cells);

		const portfolioData = data.displayConfig.transpose ? data.tableData[0].headersX : data.tableData[0].headersY;
		const portfolios = portfolioNames ? portfolioNames : portfolioData;
		const categories = data.displayConfig.transpose ? data.tableData[0].headersY : data.tableData[0].headersX;

		return cellsData.map((row: Array<CellData>, index: number) => {
			const name = portfolioNames ? get(portfolios, `[${index}]`) : get(portfolios, `[${index}].formattedValue`);

			const stackedValues = row[0]?.attribute?.stackedValues;
			return stackedValues.map((stackedValue, stackedValueIndex) => {
				return {
					name: name + ' ' + stackedValue.name,
					stack: name,
					data: row
						.map((cellData: CellData, cellIndex: number) => {
							if (get(cellData, 'config.visible')) {
								const tooltip =
									`<div class="tooltip row text-12"><span class="col-16"><b>${categories[cellIndex]?.formattedValue + ' ' + stackedValue.name}</b></span>` +
									`<span class="col-8 text-align-right">${cellData.attribute?.stackedValues?.[stackedValueIndex]?.formattedValue}</span></div>`;

								return {
									y: cellData.attribute?.stackedValues?.[stackedValueIndex]?.rawValue ?
										cellData.attribute.stackedValues[stackedValueIndex].rawValue as number * multiplier :
										0,
									yFormatted: cellData.attribute?.stackedValues?.[stackedValueIndex]?.formattedValue,
									tooltip: tooltip
								};
							} else {
								return null;
							}
						})
						.filter(row => row !== null) // filter out visible = false cell
				};
			});
		}).flat();
	}

	public static getDefaultBarSeriesForTabData(data: TabData, portfolioNames?: string[], multiplier: number = 1, customAttributes?: any): Array<any> {
		const cellsData = unzip(data?.tableData?.[0]?.cells);

		const portfolioData = data.displayConfig.transpose ? data.tableData[0].headersX : data.tableData[0].headersY;
		const portfolios = portfolioNames ? portfolioNames : portfolioData;
		const categories = data.displayConfig.transpose ? data.tableData[0].headersY : data.tableData[0].headersX;

		return cellsData.map((row: Array<CellData>, index: number) => {
			return {
				name: portfolioNames ? get(portfolios, `[${index}]`) : get(portfolios, `[${index}].formattedValue`),
				data: row
					.map((cellData: CellData, cellIndex: number) => {
						if (get(cellData, 'config.visible')) {
							let tooltip: string;
							const contributionTooltipTabData: TabData = get(customAttributes, 'contributionTooltipTabData');
							if (contributionTooltipTabData) {
								tooltip = this.getContributionTooltip(contributionTooltipTabData, cellIndex, index, cellData, categories);
							} else {
								tooltip =
									`<div class="tooltip row text-12"><span class="col-16"><b>${categories[cellIndex]?.formattedValue}</b></span>` +
									`<span class="col-8 text-align-right">${cellData.formattedValue}</span></div>`;
							}
							return {
								y: cellData.rawValue ? cellData.rawValue as number * multiplier : 0,
								yFormatted: cellData.formattedValue,
								tooltip: tooltip
							};
						} else {
							return null;
						}
					})
					.filter(row => row !== null) // filter out visible = false cell
			};
		});
	}

	public static getBarSeriesForCombinedTabData(data: TabData[], portfolioNames: string[], multiplier: number = 1): Array<any> {
		const cellsData = data.map(tabData => {
			const cells = get(tabData, 'tableData[0].cells');
			return unzip(cells)[0];
		});
		const portfolios = portfolioNames;
		const categories = get(data, '[0].displayConfig.transpose') ? get(data, '[0].tableData[0].headersY') : get(data, '[0].tableData[0].headersX');
		return cellsData.map((row: Array<CellData>, index: number) => {
			return {
				name: portfolios?.[index],
				data: row
					.map((cellData: CellData, cellIndex: number) => {
						if (get(cellData, 'config.visible')) {
							const tooltip =
								`<div class="tooltip row text-12"><span class="col-16"><b>${get(categories, [cellIndex, 'formattedValue'])}</b></span>` +
								`<span class="col-8 text-align-right">${get(cellData, 'formattedValue')}</span></div>`;
							return {
								y: get(cellData, 'rawValue') * multiplier,
								yFormatted: get(cellData, 'formattedValue'),
								tooltip: tooltip
							};
						} else {
							return null;
						}
					})
					.filter(row => row !== null)
			};
		});
	}

	/**
	 * Get the default pie series for the provided tabData, batch and index.
	 *
	 * @param {TabData} data the configured data after the initial API data request has been resolved.
	 * @param {number} dataIndex the index of the portfolio to be processed (defaulted to 0)
	 * @param {number} dataBatch the table data batch (defaulted to 0)
	 */
	public static getDefaultPieSeriesForTabData(data: TabData, dataIndex: number = 0, dataBatch: number = 0): Array<any> {
		const portfolios = data.displayConfig.transpose ? data.tableData[dataBatch].headersX : data.tableData[dataBatch].headersY;
		const categories = data.displayConfig.transpose ? data.tableData[dataBatch].headersY : data.tableData[dataBatch].headersX;
		const seriesDataArray = data.tableData[dataBatch].cells
			.map((row: Array<CellData>, index: number) => {
				return {
					category: categories[index].formattedValue,
					formattedY: row[dataIndex].formattedValue,
					y: row[dataIndex].rawValue,
					tooltip: `<div class="text-12"><span class="text-bold">${categories[index].formattedValue}</span><span> ${row[dataIndex].formattedValue}</span></div>`
				};
			})
			.filter(series => series.y !== undefined);

		let seriesSum = 0;
		seriesDataArray.forEach(series => (seriesSum += Number(series.y)));
		if (seriesSum < 100) {
			seriesDataArray.push({
				category: 'EMPTY',
				formattedY: String(100 - seriesSum),
				y: 100 - seriesSum,
				tooltip: `<div class="text-12"><span class="text-bold">EMPTY</span><span> ${100 - seriesSum}</span></div>`
			});
		}

		return [
			{
				name: portfolios[dataIndex].formattedValue,
				data: seriesDataArray
			}
		];
	}

	public static getDefaultScatterSeriesForTabData(data: TabData[], customisations?: Array<ScatterChartSeriesCustomisations>): Array<any> {
		return data.reduce((acc: Array<any>, tab: TabData, i: number) => {
			const portfolios = tab.displayConfig.transpose ? tab.tableData[0].headersX : tab.tableData[0].headersY;
			const series = get(tab, 'tableData[0].cells', [])
				.map((cell: CellData[], j: number) => {
					const name: string = ChartFactory.applySeriesNameCustomisations(portfolios[j].formattedValue, i, customisations);
					const color: string = get(customisations, `[${i}].colors[${j}]`);
					const yVal: CellData = cell[0];
					const xVal: CellData = cell[1];

					// condition for type checking only
					if (typeof xVal.rawValue === 'number' && typeof yVal.rawValue === 'number') {
						// Each cell contains the the x and y data for a single portfolio
						// cell[0] = y, cell[1] = x
						return {
							name,
							data: [[xVal.rawValue ? xVal.rawValue * 100 : null, yVal.rawValue ? yVal.rawValue * 100 : null]],
							color
						};
					}
				});
			acc.push(...series);
			return acc;
		}, []);
	}

	public static getDefaultLineSeriesForTabData(data: TabData[], multiplier: number = 10000, customisations?: Array<LineChartSeriesCustomisations>): Array<any> {
		return data.reduce((acc: Array<any>, tab: TabData, i: number) => {
			const cellsData = get(tab, 'tableData[0].cells', []).map(cell => cell[0]);
			const portfolios = tab.displayConfig.transpose ? tab.tableData[0].headersX : tab.tableData[0].headersY;
			const series = cellsData
				.map((cell: CellData, j: number) => {
					if (cell.rawValue instanceof Array) { // always true -- put here for type checking only
						const name: string = ChartFactory.applySeriesNameCustomisations(portfolios[j].formattedValue, i, customisations);
						const color: string = get(customisations, `[${i}].colors[${j}]`);

						return {
							name,
							data: cell.rawValue.map(item => [item[0], item[1] * multiplier]),
							color
						};
					}
				})
				.filter(series => series);
			acc.push(...series);
			return acc;
		}, []);
	}

	public static applySeriesNameCustomisations(name: string, tabIndex: number, customisations: Array<LineChartSeriesCustomisations>): string {
		let customisedName = name;
		if (customisations && customisations[tabIndex]) {
			const prefix = customisations[tabIndex].prefix;
			const portfolioNameTemplate = customisations[tabIndex].tooltipPortfolioNameTemplate;
			const tooltipPortfolioNameLength = customisations[tabIndex].tooltipPortfolioNameLength;

			if (prefix && portfolioNameTemplate) {
				customisedName = StringUtil.fillTemplate(portfolioNameTemplate, { prefix, portfolio: name });
				if (tooltipPortfolioNameLength && name.length - 3 > tooltipPortfolioNameLength) {
					customisedName = `${customisedName.substring(0, tooltipPortfolioNameLength)}...`;
				}
			}
		}

		return customisedName;
	}

	public static getDefaultLineSeriesForTabDataPas(data: TabData[], multiplier: number = 10000, customisations?: Array<LineChartSeriesCustomisations>): Array<any> {
		return data.reduce((acc: Array<any>, tab: TabData, i: number) => {
			const series = get(tab, 'tableData', []).map((tableData, j) => {
				const portfolioName = get(tableData, 'headersY[0].formattedValue');
				const name: string = ChartFactory.applySeriesNameCustomisations(portfolioName, i, customisations);
				const color: string = get(customisations, `[${i}].colors[${j}]`);

				const data = get(tableData, 'cells', [])
					.filter(cell => {
						const returnValue = Number(get(cell, '[1].rawValue'));
						return !isNaN(returnValue) && !isNil(returnValue);
					})
					.map(cell => {
						const timestamp = Date.parse(String(get(cell, '[0].rawValue')));
						const hypoAmount = get(cell, '[1].rawValue') * multiplier;

						return [timestamp, hypoAmount];
					});
				return { name, color, data };
			});

			acc.push(...series);
			return acc;
		}, []);
	}

	public static getRollingLineSeriesForTabData(data: TabData, multiplier: number = 10000): Array<any> {
		return get(data, 'tableData', []).map((tableData) => {
			const data = get(tableData, 'cells', [])
				.filter(cell => {
					const returnValue = Number(get(cell, '[1].rawValue'));
					return !isNaN(returnValue) && !isNil(returnValue);
				})
				.map(cell => {
					const timestamp = Date.parse(String(get(cell, '[0].rawValue')));
					const hypoAmount = get(cell, '[1].rawValue') * multiplier;
					return [timestamp, hypoAmount];
				});
			return {
				name: get(tableData, 'headersY[0].formattedValue'),
				data
			};
		});
	}

	public static getDefaultColumnTotalForTabData(data: TabData): Array<string> {
		const headers = data.displayConfig.transpose ? data.tableData[0].headersY : data.tableData[0].headersX;
		const cells = data.tableData[0].cells.filter((row: Array<CellData>, index: number) => !headers[index]?.attribute?.ignoreInChart);
		const sumArr: Array<number> = [];
		const unzippedArr = unzip(cells);
		unzippedArr.forEach((cell: Array<CellData>, index: number) => {
			sumArr.push(cell.reduce((sum: number, cellData: CellData) => sum + Number(cellData.rawValue), 0));
		});
		return sumArr.map(item => {
			if (cells[0][0].config && cells[0][0].config.labelFormatter) {
				const label = String(cells[0][0].config.labelFormatter.format(item));
				return label;
			} else {
				return item.toString();
			}
		});
	}

	public static getPortfolioNamesForTabData(data: TabData): Array<any> {
		const headers = data.displayConfig.transpose ? data.tableData[0].headersX : data.tableData[0].headersY;
		return headers.map((cellData: CellData) => cellData.formattedValue);
	}

	/**
	 * Get the default pie title for the provided tabData, batch and index.
	 *
	 * @param {TabData} data the configured data after the initial API data request has been resolved.
	 * @param {number} dataIndex the index of the Portfolio to be processed (defaulted to 0)
	 * @param {number} dataBatch the table data batch (defaulted to 0)
	 */
	public static getDefaultPieTitleForTabData(data: TabData, dataIndex: number = 0, dataBatch: number = 0): string {
		const headers = data.displayConfig.transpose ? data.tableData[dataBatch].headersX : data.tableData[dataBatch].headersY;
		return headers[dataIndex].formattedValue;
	}

	public static getCategoriesForTabData(data: TabData, tableDataIndex: number = 0): Array<any> {
		const headers = data.displayConfig.transpose ? data.tableData[tableDataIndex].headersY : data.tableData[tableDataIndex].headersX;
		return headers.map((cellData: CellData) => cellData.formattedValue);
	}

	/**
	 * @description Table headers are grouped by id and return the formattedValue of first key of groupedHeaders
	 * @param data
	 * @returns: string[] of categories that will form the tickers on x-axis in the chart
	 */
	public static getCategoriesForHeaderIds(data: TabData): string[] {
		const headers = data.displayConfig.transpose ? data.tableData[0].headersY : data.tableData[0].headersX;
		const groupedHeaders: Map<string, CellData[]> = new Map(Object.entries(groupBy(headers, (cell) => cell?.config?.['id'])));

		const categories = groupedHeaders.values().next().value.map((element: CellData) => {
			return element.formattedValue;
		});
		return categories;
	}

	/**
	 * Currently used in IQ specific charts  - upside/downside chart, Historical capital gains
	 * Grouped the tab data by id and return columnDataArray which contains name and data,
	 * name - id of column in tabData
	 * data -  columnData which contains grouped data by id and tooltip
	 * buildSeriesTooltip - flag true when series tooltip is required (when overriding global chart tooltip) e.g. Upside/downside chart
	 * tooltipData - supplemntary data for use in tooltip formatter e.g. Historical capital gains
	 * @param {TabData} data the configured data after the initial API data request has been resolved.
	 */
	public static getIqColumnSeriesForHeaderIds(data: TabData, buildSeriesTooltip: boolean, tooltipData?: string[]): Array<any> {
		const columnDataArray = [];
		let columnData;
		let tooltip;

		const cellsData = unzip(data?.tableData?.[0]?.cells)[0];
		const cellsGroup: Map<string, any> = new Map(Object.entries(groupBy(cellsData, (cell) => cell?.config?.['id'])));
		const headersYData = data?.tableData?.[0]?.headersY;
		const headersYGroup: Map<string, any> = new Map(Object.entries(groupBy(headersYData, (headersY) => headersY?.config?.['id'])));

		cellsGroup.forEach((value, key) => {
			columnData = Array.from(value).map((cell: CellData, index: number) => {
				if (buildSeriesTooltip) {
					tooltip =
						`<div class="row d-flex text-12" style="width:180px;margin-top:10px;">
				       		<div class="col-1">
					      		<div class="portfolio-icon" style="background-color:${cell.config?.label?.attributes?.colorTheme}">
								</div>
			           		</div>
							<div class="col-2 text-bold" style="margin-left:-7px;">${headersYGroup.get(key)?.[index]?.formattedValue}</div>
			      			<div class="col-10" style="margin-left:3px;">${key}</div>
			      			<div class="col-8 text-align-right">
							  ${cell?.config?.labelFormatter ? cell.config.labelFormatter?.format(cell.formattedValue) : cell.formattedValue}%
							</div>
			     		</div>`;
				}
				return { y: +cell.formattedValue, tooltip, tooltipData: tooltipData?.[index] };
			});
			columnDataArray.push({ name: key, data: columnData });
		});
		return columnDataArray;
	}

	/**
	 * Currently used in PA specific charts  - client proposal upside/downside chart and client proposal performance chart
	 * @param {TabData} data the configured data after the initial API data request has been resolved.
	 */
	public static getPaColumnSeriesForForHeaderIds(data: TabData): Array<any> {
		const columnDataArray = [];
		const cellsArray: CellData[][] = data?.tableData?.[0]?.cells;

		let columnData;
		cellsArray?.forEach((cellsData: CellData[], index) => {
			const cellsGroup: Map<string, any> = new Map(Object.entries(groupBy(cellsData, (cell) => cell?.config?.['id'])));
			const name = data?.tableData?.[0]?.headersY?.[index]?.formattedValue;
			cellsGroup.forEach((group, key) => {
				columnData = Array.from(group).map((cell: CellData) => {
					return { y: cell.rawValue };
				});
				columnDataArray.push({ name: key + ' ' + name, data: columnData });
			});
		});
		return columnDataArray;
	}

	/**
	 * Get the default colors for the provided tabData, batch and index.
	 *
	 * @param {TabData} data the configured data after the initial API data request has been resolved.
	 * @param {number} portfolioIndex the index of the Portfolio to be processed (defaulted to 0)
	 * @param {number} dataBatch the table data batch (defaulted to 0)
	 */
	public static getDefaultColorsForTabData(data: TabData, portfolioIndex: number = 0, dataBatch: number = 0): Array<string> {
		const indicesToBeRemoved: number[] = [];
		let headers = data.displayConfig.transpose ? data.tableData[dataBatch].headersY : data.tableData[dataBatch].headersX;
		headers = headers.filter(header => !header.attribute.ignoreInChart);

		// filter out the colors for the categories with no holding weight
		data.tableData[dataBatch].cells.forEach((cell, index) => {
			if (cell[portfolioIndex] && cell[portfolioIndex].rawValue === undefined) {
				indicesToBeRemoved.push(index);
			}
		});

		headers = headers.filter((header, index) => !indicesToBeRemoved.includes(index));
		return headers.map((cellData: CellData) => (cellData.attribute && cellData.attribute.colorTheme) || '#ccc');
	}

	public static getDefaultColorsForTabDataReversed(data: TabData): Array<string> {
		let headers = data.displayConfig.transpose ? data.tableData[0].headersY : data.tableData[0].headersX;
		headers = headers.filter(header => !header.attribute?.ignoreInChart).reverse();
		return headers.map((cellData: CellData) => (cellData.attribute && cellData.attribute.colorTheme) || '#ccc');
	}

	public static getPortfolioColors(): Array<string> {
		return ColorsConfig.PORTFOLIO_COLORS;
	}

	public static getExposureColorsForTabData(data: TabData): Array<string> {
		const headers = data.displayConfig.transpose ? data.tableData[0].headersY : data.tableData[0].headersX;
		return headers
			.filter((cellData: CellData) => !cellData.parent && !cellData.attribute?.ignoreInChart)
			.map((cellData: CellData) => (cellData.attribute && cellData.attribute.colorTheme) || '#ccc')
			.reverse();
	}

	/**
	 * getStackedData: gets stacked data.
	 * @param {TabData} data the configured data after the initial API data request has been resolved.
	 * @param {number} portfolioIndex the index of the Portfolio to be processed.
	 */
	public static getStackedData(data: TabData, portfolioIndex?: number): IChartData {
		const chartData: IChartData = {} as IChartData;
		// omit categories that have the attribute 'ignoreInChart'
		chartData.scenarios = data.tableData[0].headersX.filter(header => !(header.attribute && header.attribute.ignoreInChart)).map(category => category.formattedValue);

		// omit colors that have the attribute 'ignoreInChart'
		chartData.colors = data.tableData[0].headersX.filter(header => !(header.attribute && header.attribute.ignoreInChart)).map(category => category.attribute.colorTheme);
		try {
			const tableData = head(data.tableData);
			chartData.series = [];
			chartData.portfolios = [tableData.headersY[portfolioIndex].formattedValue];
			chartData.scenarios.forEach(filter => {
				const seriesData: ISeriesData = {} as ISeriesData;
				seriesData.name = filter;
				seriesData.data = this.getStackedDataByFilter(tableData, filter, chartData.scenarios.length, portfolioIndex);
				chartData.series.push(seriesData);
			});
		} catch (error) {
			console.error('Cannot get Chart Data from TabData', error);
		}
		return chartData;
	}

	/**
	 * TODO wbanther: REMOVE. This is a temporary fix. Duplicating method from refactored HS lib. Don't want to create circular reference. This method will be removed once
	 * HS lib is being used to generate PDFs.
	 */
	public static getStackedColumnData(tabData: TabData, portfolioIndex?: number): IScenarioAnalysisData {
		const scenarioAnalysisData: IScenarioAnalysisData = {} as IScenarioAnalysisData;
		try {
			const tableData = head(tabData.tableData);
			scenarioAnalysisData.scenarios = tableData.headersX.filter(header => header.config.id === 'scenario').map(header => header.formattedValue);
			scenarioAnalysisData.series = [];
			scenarioAnalysisData.portfolios = [];

			let portfolioNames = tableData.headersY.map(header => header.formattedValue);
			// Fringe scenario: we have portfolios with identical names we'll need to ensure they are unique
			if (this.hasDuplicateNames(portfolioNames)) {
				portfolioNames = this.createUniqueNames(portfolioNames);
			}

			if (typeof portfolioIndex === 'number' && portfolioIndex > -1) {
				scenarioAnalysisData.portfolios.push(portfolioNames[portfolioIndex]);
			} else {
				scenarioAnalysisData.portfolios = portfolioNames;
			}

			if (typeof portfolioIndex === 'number' && portfolioIndex > -1) {
				Object.keys(ASSET_CLASSES).forEach(assetClass => {
					const scenarioChartData: IScenarioChartData = {} as IScenarioChartData;
					scenarioChartData.name = ASSET_CLASSES[assetClass];
					scenarioChartData.stack = scenarioAnalysisData.portfolios[0];
					scenarioChartData.data = this.getAssetClassValues(tableData, assetClass, scenarioAnalysisData.scenarios.length, portfolioIndex);
					scenarioAnalysisData.series.push(scenarioChartData);
				});
			} else {
				scenarioAnalysisData.portfolios.forEach((portfolio, index) => {
					Object.keys(ASSET_CLASSES).forEach(assetClass => {
						const scenarioChartData: IScenarioChartData = {} as IScenarioChartData;
						scenarioChartData.name = ASSET_CLASSES[assetClass];
						scenarioChartData.stack = portfolio;
						scenarioChartData.data = this.getAssetClassValues(tableData, assetClass, scenarioAnalysisData.scenarios.length, index);
						scenarioAnalysisData.series.push(scenarioChartData);
					});
				});
			}
		} catch (error) {
			console.error('Cannot get ScenarioAnalysis Data from TabData ', error);
		}
		return scenarioAnalysisData;
	}

	/**
	 * @description Extracts AssetClass values for each scenario from Tabledata and returns an array
	 *
	 * @author aatray
	 * @private
	 * @param {TableData} tableData
	 * @param {string} assetClass
	 * @param {number} noOfScenarios
	 * @param {number} [indexPosition=0]
	 * @returns {number[]}
	 * @memberof ScenarioAnalysisUtil
	 */
	private static getAssetClassValues(tableData: TableData, assetClass: string, noOfScenarios: number, indexPosition = 0): number[] {
		let assetClassValues = [];
		const assetClassIndices = [];
		try {
			let assetClassIndex = findIndex(tableData.headersX, header => header.rawValue === assetClass);
			if (assetClassIndex <= -1) {
				assetClassValues = new Array(noOfScenarios).fill(0);
			} else {
				if (assetClassIndex > -1) {
					assetClassIndices.push(assetClassIndex);
				}
				while (assetClassIndex > -1) {
					assetClassIndex = findIndex(tableData.headersX, header => header.rawValue === assetClass, ++assetClassIndex);
					if (assetClassIndex > -1) {
						assetClassIndices.push(assetClassIndex);
					}
				}
				assetClassIndices.forEach(index => {
					assetClassValues.push(get(tableData.cells[indexPosition][index], 'rawValue', 0));
				});
			}
		} catch (error) {
			console.error('Cannot get AssetClass Values from TabData ', error);
		}
		return assetClassValues;
	}

	/**
	 * getStackedPieData: gets stacked data.
	 * @param {TabData} data the configured data after the initial API data request has been resolved.
	 * @param {number} portfolioIndex the index of the Portfolio to be processed.
	 */
	public static getStackedPieData(data: TabData, portfolioIndex?: number): IChartData {
		const chartData: IChartData = {} as IChartData;
		let pieData = [];
		try {
			chartData.profileName = data.tableData[0].headersY[portfolioIndex].formattedValue;
			// omit categories that have the attribute 'ignoreInChart'
			chartData.scenarios = data.tableData[0].headersX.filter(header => !(header.attribute && header.attribute.ignoreInChart)).map(category => category.formattedValue);

			// omit colors that have the attribute 'ignoreInChart'
			chartData.colors = data.tableData[0].headersX.filter(header => !(header.attribute && header.attribute.ignoreInChart)).map(category => category.attribute.colorTheme);
			const tableData = head(data.tableData);
			let pieDataItem = null;
			chartData.scenarios.forEach(filter => {
				pieDataItem = this.getStackedPieDataByFilter(tableData, filter, chartData.scenarios.length, portfolioIndex);
				pieData = pieData.concat(pieDataItem);
			});
		} catch (error) {
			console.error('Cannot get Chart Data from TabData', error);
		}
		chartData.series = pieData;
		return chartData;
	}

	private static getStackedDataByFilter(data: TableData, filter: string, noOfScenarios: number, indexPosition = 0): number[] {
		let dataValues = [];
		try {
			let dataIndex = findIndex(data.headersX, header => header.formattedValue === filter && (header.attribute && header.attribute.ignoreInChart !== true));
			if (dataIndex <= -1) {
				dataValues = new Array(noOfScenarios).fill(0);
			} else {
				const dataIndices = [];
				while (dataIndex > -1) {
					dataIndices.push(dataIndex);
					dataIndex = findIndex(data.headersX, header => header.formattedValue === filter, ++dataIndex);
				}
				dataIndices.forEach(index => {
					dataValues.push({ y: get(data.cells[indexPosition][index], 'rawValue', 0) });
				});
			}
		} catch (error) {
			console.error('Cannot get Values By Filter from TabData', error);
		}
		return dataValues;
	}

	private static getStackedPieDataByFilter(data: TableData, filter: string, noOfScenarios: number, indexPosition = 0): number[] {
		let dataValues = [];
		let dataItem = null;
		const pieDataPoint: IPieSeriesData = {} as IPieSeriesData;
		try {
			let dataIndex = findIndex(data.headersX, header => header.formattedValue === filter && (header.attribute && header.attribute.ignoreInChart !== true));
			if (dataIndex <= -1) {
				dataValues = new Array(noOfScenarios).fill(0);
			} else {
				const dataIndices = [];
				while (dataIndex > -1) {
					dataIndices.push(dataIndex);
					dataIndex = findIndex(data.headersX, header => header.formattedValue === filter, ++dataIndex);
				}
				dataIndices.forEach(index => {
					dataItem = get(data.cells[indexPosition][index], 'rawValue', 0);
					pieDataPoint.y = dataItem ? dataItem : 0;
					pieDataPoint.name = filter;
					dataValues.push(pieDataPoint);
				});
			}
		} catch (error) {
			console.error('Cannot get Values By Filter from TabData', error);
		}
		return dataValues;
	}

	public static createUniqueNames(names: Array<string>): string[] {
		names.forEach(name => {
			let numberOfOccurrences = -1;
			for (let i = 0; i < names.length; i++) {
				if (names[i] === name) {
					numberOfOccurrences++;
				}
				if (names[i] === name && numberOfOccurrences >= 1) {
					names[i] = `${name} (${numberOfOccurrences})`;
				}
			}
		});
		return names;
	}

	public static hasDuplicateNames(names: Array<string>): boolean {
		const originalLength = names.length;
		const uniqueNamesLength = names.filter((name, index, self) => {
			return self.indexOf(name) === index;
		}).length;
		return originalLength !== uniqueNamesLength;
	}

	public static getContributionTooltip(
		contributionTooltipTabData: TabData,
		rowIndex: number,
		columnIndex: number,
		originalCellData: CellData,
		categories: CellData[],
		productNameCharacterLimit?: number
	): string {
		// data prep
		const cellsData = get(contributionTooltipTabData, `tableData[0].cells`);
		const matchedIndex = get(contributionTooltipTabData, `tableData[0].headersY`, []).findIndex(
			headerY => headerY.rawValue === categories[rowIndex].rawValue || headerY.formattedValue === categories[rowIndex].formattedValue);
		const contributors: {
			topContributors: ContributorData[];
			topDetractors: ContributorData[];
			topContributorsLabel: string;
			topDetractorsLabel: string;
		} = get(cellsData, `${matchedIndex}.${columnIndex}.rawValue`);

		// static part of tooltip
		let tooltip = `<div class="tooltip row text-12">` +
			`<span class="col-16 text-bold">${categories[rowIndex].formattedValue}</span>` +
			`<span class="col-8 text-align-right align-content-right">${originalCellData.formattedValue}</span>`
			;

		if (contributors) {
			// render top contributors part, check if any data is more than 0.005 to show
			if (get(contributors, 'topContributors', []).length) {
				let topContributorsTooltip = '';
				contributors.topContributors.forEach(contributor => {
					const contributorRoundedValue = NumberUtil.precisionRound(contributor.relativeY, 2);
					if (Math.abs(contributorRoundedValue) > 0) {
						if (contributor?.isDescriptionVisible) {
							topContributorsTooltip +=
								`<span class="col-8 text-10 text-margin-bottom">${contributor[contributor['identifier']] ?? '-'}</span>` +
								`<span class="col-12 text-10 text-margin-bottom">${(contributor.description.length > productNameCharacterLimit) ? contributor.description.substring(0, productNameCharacterLimit) + '...' : contributor.description}</span>` +
								`<span class="col-4 text-align-right text-10 text-margin-bottom">${NumberUtil.formatWithPrecisionRounding(contributor.relativeY, 2)}%</span>`;
						}
						else {
							topContributorsTooltip += `<span class="col-16 text-10">${contributor[contributor['identifier']]}</span>` +
								`<span class="col-8 text-align-right text-10">${NumberUtil.formatWithPrecisionRounding(contributor.relativeY, 2)}%</span>`;
						}
					}
				});
				if (topContributorsTooltip) {
					tooltip += `<span class="col-24 padding-top-5 text-10">${contributors.topContributorsLabel || 'Top Contributors'}<div class="table-title-separator-half"></div></span>`
						+ topContributorsTooltip;
				}
			}

			// render top detractors part, check if any data is less than -0.005 to show
			if (get(contributors, 'topDetractors', []).length) {
				let topDetractorsTooltip = '';
				contributors.topDetractors.forEach(contributor => {
					const contributorRoundedValue = NumberUtil.precisionRound(contributor.relativeY, 2);
					if (Math.abs(contributorRoundedValue) > 0) {
						if (contributor?.isDescriptionVisible) {
							topDetractorsTooltip +=
								`<span class="col-8 text-10 text-margin-bottom">${contributor[contributor['identifier']] ?? '-'}</span>` +
								`<span class="col-12 text-10 text-margin-bottom">${(contributor.description.length > 50) ? contributor.description.substring(0, 50) + '...' : contributor.description}</span>` +
								`<span class="col-4 text-align-right text-10 text-margin-bottom">${NumberUtil.formatWithPrecisionRounding(contributor.relativeY, 2)}%</span>`;
						}
						else {
							topDetractorsTooltip += `<span class="col-16 text-10">${contributor[contributor['identifier']]}</span>` +
								`<span class="col-8 text-align-right text-10">${NumberUtil.formatWithPrecisionRounding(contributor.relativeY, 2)}%</span>`;
						}
					}
				});
				if (topDetractorsTooltip) {
					tooltip += `<span class="col-24 padding-top-10 text-10">${contributors.topDetractorsLabel || 'Top Detractors'}<div class="table-title-separator-half"></div></span>`
						+ topDetractorsTooltip;
				}
			}
		}

		return tooltip;
	}

	/**
	 * Used to attach auxiliary series data points to the chart series
	 * These data points can be accessed in the tooltip via this.point.series.userOptions.auxiliaryData.x
	 * An example can be found in AssetAllocationProjectedRiskReturnComponent
	 *
	 * Assigns the data based on the index meaning you need to ensure that auxiliaryData[0] is intended for seriesData[0]
	 *
	 * Types are any as they can vary by chartType and we have not typed the series object of the different supported chartTypes
	 */
	public static enrichSeriesWithAuxiliaryData(seriesData: any[], auxiliaryData: any[]): any[] {
		return seriesData.map((series, i) => {
			return { ...series, auxiliaryData: auxiliaryData[i] };
		});
	}
}
